import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, Wrench, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { ROLE_HOME } from "../../lib/gmaoConstants";
import RobotsTag from "../seo/RobotsTag";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card } from "../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const LoginPage = () => {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    company: "",
    role: "client",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Déjà connecté → rediriger vers son espace
  if (user && user !== false) {
    return <Navigate to={ROLE_HOME[user.role] || "/"} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    let res;
    if (mode === "login") {
      res = await login(form.email, form.password);
    } else {
      res = await register({
        name: form.name,
        email: form.email,
        password: form.password,
        phone: form.phone || null,
        company: form.role === "agence" ? form.company || null : null,
        role: form.role,
      });
    }
    setLoading(false);
    if (res.ok) {
      toast.success(mode === "login" ? "Connexion réussie" : "Compte créé avec succès");
      navigate(ROLE_HOME[res.user.role] || "/", { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[hsl(215,25%,15%)] px-4 py-10">
      <RobotsTag content="noindex, nofollow" />
      <Helmet>
        <title>Connexion – Espace privé NGL SERVICES</title>
      </Helmet>

      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm mb-6 transition-colors"
          data-testid="back-to-site-link"
        >
          <ArrowLeft className="w-4 h-4" /> Retour au site
        </Link>

        <Card className="p-8 shadow-2xl border-0">
          <div className="flex flex-col items-center mb-6">
            <div className="bg-[hsl(207,78%,42%)] rounded-xl p-3 mb-3">
              <Wrench className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[hsl(215,25%,15%)]">
              {mode === "login" ? "Connexion" : "Créer un compte"}
            </h1>
            <p className="text-sm text-muted-foreground mt-1 text-center">
              Portail Interventions NGL SERVICES
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="role">Vous êtes</Label>
                  <Select value={form.role} onValueChange={(v) => set("role", v)}>
                    <SelectTrigger data-testid="register-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client">Client particulier</SelectItem>
                      <SelectItem value="agence">Agence / Syndic / Professionnel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Nom complet</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    required
                    data-testid="register-name-input"
                  />
                </div>
                {form.role === "agence" && (
                  <div className="space-y-2">
                    <Label htmlFor="company">Société / Agence</Label>
                    <Input
                      id="company"
                      value={form.company}
                      onChange={(e) => set("company", e.target.value)}
                      data-testid="register-company-input"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    data-testid="register-phone-input"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                required
                minLength={6}
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full btn-primary rounded-lg"
              disabled={loading}
              data-testid="auth-submit-button"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {mode === "login" ? "Se connecter" : "Créer mon compte"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            {mode === "login" ? (
              <button
                type="button"
                className="text-[hsl(207,78%,42%)] hover:underline"
                onClick={() => setMode("register")}
                data-testid="switch-to-register"
              >
                Pas encore de compte ? Créer un compte
              </button>
            ) : (
              <button
                type="button"
                className="text-[hsl(207,78%,42%)] hover:underline"
                onClick={() => setMode("login")}
                data-testid="switch-to-login"
              >
                Déjà un compte ? Se connecter
              </button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
